import { Suspense, lazy, useEffect, useState, createContext, useContext, Component } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { canAccessAnyRole, getDefaultRouteForRole, normalizeRole, ROLES } from "./utils/rbac";

const Index = lazy(() => import("./Home/Home"));
const Login = lazy(() => import("./pages/SimpleLogin"));
const CompanyOnboardingWizard = lazy(() => import("./pages/saas/CompanyOnboardingWizard"));
const SubscriptionCenter = lazy(() => import("./pages/saas/SubscriptionCenter"));
const AboutUs = lazy(() => import("./Home/AboutUs"));
const Services = lazy(() => import("./Home/Services"));
const Plans = lazy(() => import("./Home/Plans"));
const Blogs = lazy(() => import("./Home/Blogs"));
const ContactUs = lazy(() => import("./Home/ContactUs"));
const AdminLayout = lazy(() => import("./components/layouts/AdminLayout"));
const EmployeeLayout = lazy(() => import("./components/layouts/EmployeeLayout"));
const SuperAdminLayout = lazy(() => import("./components/layouts/SuperAdminLayout"));

const LoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 to-slate-800">
    <div className="text-center">
      <div className="inline-flex items-center justify-center">
        <div className="w-12 h-12 rounded-lg bg-orange-500 opacity-50 animate-pulse" />
      </div>
      <p className="text-sm text-gray-400 mt-4">Loading...</p>
    </div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes instead of 1
      refetchOnWindowFocus: false,
      retry: 1,
      gcTime: 10 * 60 * 1000, // Increased cache time
    },
  },
});

const getStoredUser = () => {
  try {
    const readCandidate = (storage, key) => {
      if (typeof window === "undefined" || !storage) return null;
      const raw = storage.getItem(key);
      const parsed = raw ? JSON.parse(raw) : null;
      if (!parsed || typeof parsed !== "object") return null;

      const expiresAt = Number(parsed.expiresAt || 0);
      if (expiresAt && Date.now() > expiresAt) {
        try {
          storage.removeItem(key);
        } catch {}
        try {
          window.dispatchEvent(new Event("sintech:auth-change"));
        } catch {}
        return null;
      }
      return parsed;
    };

    // Prefer tab-scoped impersonation session when present (does not affect other tabs).
    const parsed =
      readCandidate(window.sessionStorage, "sintech_user_tab") ||
      readCandidate(window.localStorage, "sintech_user");
    if (!parsed) return null;

    const idLike = String(parsed.employeeId || parsed.id || "").toUpperCase();
    const rawRole = String(parsed.role || parsed.userRole || "").trim();
    const storedRole = rawRole ? normalizeRole(rawRole) : "";
    const fallbackRole = idLike.startsWith("ADMIN") ? ROLES.LEGACY_ADMIN : ROLES.EMPLOYEE;
    const role = storedRole || fallbackRole;
    const hasIdentity = !!(parsed.employeeId || parsed.id || parsed.email);
    if (!hasIdentity) return null;
    return { ...parsed, role };
  } catch {
    return null;
  }
};

// Create auth context for reactive user state
const AuthContext = createContext(null);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => getStoredUser());

  useEffect(() => {
    // Listen for storage changes or custom auth events
    const handleStorageChange = () => {
      const updatedUser = getStoredUser();
      setUser(updatedUser);
    };

    // Handle custom auth event from Login component
    const handleAuthChange = () => {
      const updatedUser = getStoredUser();
      setUser(updatedUser);
    };

    window.addEventListener("storage", handleStorageChange);
    window.addEventListener("sintech:auth-change", handleAuthChange);

    // Sync state once on mount.
    handleStorageChange();

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("sintech:auth-change", handleAuthChange);
    };
  }, []);

  return <AuthContext.Provider value={{ user }}>{children}</AuthContext.Provider>;
};

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    return { user: getStoredUser() };
  }
  return context;
};

const RequireRole = ({ role, children }) => {
  const { user } = useAuth();
  // Re-validate session expiry on every protected route render
  if (!user) return <Navigate to="/login" replace />;
  const expiresAt = Number(user.expiresAt || 0);
  if (expiresAt && Date.now() > expiresAt) {
    try { localStorage.removeItem("sintech_user"); sessionStorage.removeItem("sintech_user_tab"); } catch {}
    return <Navigate to="/login" replace />;
  }
  const allowed = Array.isArray(role) ? role : [role];
  if (!canAccessAnyRole(user.role, allowed)) {
    return <Navigate to={getDefaultRouteForRole(user.role)} replace />;
  }
  return children;
};

const LoginGate = () => {
  const { user } = useAuth();
  if (!user) return <Login />;
  const redirect = getDefaultRouteForRole(user.role);
  return <Navigate to={redirect} replace />;
};

class RouteErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: "" };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, message: String(error?.message || "Unknown error") };
  }

  componentDidCatch(error) {
    // eslint-disable-next-line no-console
    console.error("Route render error:", error);
    const message = String(error?.message || "");
    const isChunkError = /loading chunk|chunkloaderror|failed to fetch dynamically imported module/i.test(message);
    if (!isChunkError) return;
    try {
      const key = "sintech_chunk_reload_once";
      const alreadyReloaded = sessionStorage.getItem(key) === "1";
      if (!alreadyReloaded) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
      }
    } catch {}
  }

  handleReset = () => {
    try {
      const hasTab = sessionStorage.getItem("sintech_user_tab");
      if (hasTab) sessionStorage.removeItem("sintech_user_tab");
      else localStorage.removeItem("sintech_user");
    } catch {}
    try {
      window.dispatchEvent(new Event("sintech:auth-change"));
    } catch {}
    window.location.href = "/login";
  };

  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 p-6">
        <div className="w-full max-w-md rounded-xl border border-white/10 bg-slate-900 p-6 text-center">
          <h2 className="text-white text-xl font-semibold">Page failed to load</h2>
          <p className="text-slate-300 text-sm mt-2">Please reload or sign in again.</p>
          {this.state.message ? (
            <p className="text-slate-400 text-xs mt-2 break-words">{this.state.message}</p>
          ) : null}
          <div className="mt-4 flex gap-2 justify-center">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-lg bg-slate-700 text-white text-sm"
            >
              Reload
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="px-4 py-2 rounded-lg bg-orange-500 text-white text-sm"
            >
              Re-login
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const App = () => {
  // Login is email/password only. Do not auto-create anonymous sessions.

  useEffect(() => {
    const preloadRoutes = () => {
      import("./components/layouts/AdminLayout");
      import("./components/layouts/EmployeeLayout");
      import("./components/layouts/SuperAdminLayout");
    };

    if (window.requestIdleCallback) {
      const id = window.requestIdleCallback(preloadRoutes);
      return () => window.cancelIdleCallback?.(id);
    }
    const timer = setTimeout(preloadRoutes, 600);
    return () => clearTimeout(timer);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/services" element={<Services />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/contact-us" element={<ContactUs />} />
              <Route path="/login" element={<LoginGate />} />
              <Route path="/legacy-login" element={<Navigate to="/login" replace />} />
              <Route
                path="/register-company"
                element={(
                  <RequireRole role={[ROLES.MASTER_ADMIN, ROLES.SUPER_ADMIN]}>
                    <CompanyOnboardingWizard />
                  </RequireRole>
                )}
              />
              <Route
                path="/subscription"
                element={(
                  <RequireRole role={[ROLES.LEGACY_ADMIN, ROLES.COMPANY_ADMIN, ROLES.SUPER_ADMIN, ROLES.MASTER_ADMIN]}>
                    <SubscriptionCenter />
                  </RequireRole>
                )}
              />
              <Route
                path="/admin/*"
                element={(
                  <RequireRole
                    role={[
                      ROLES.LEGACY_ADMIN,
                      ROLES.COMPANY_ADMIN,
                      ROLES.HR_MANAGER,
                      ROLES.TEAM_MANAGER,
                      ROLES.ACCOUNTANT,
                      ROLES.RECRUITER,
                      ROLES.SUPER_ADMIN,
                      ROLES.MASTER_ADMIN,
                    ]}
                  >
                    <RouteErrorBoundary>
                      <AdminLayout />
                    </RouteErrorBoundary>
                  </RequireRole>
                )}
              />
              <Route
                path="/super-admin/*"
                element={(
                  <Navigate to="/master-admin" replace />
                )}
              />
              <Route
                path="/master-admin/*"
                element={(
                  <RequireRole role={[ROLES.MASTER_ADMIN, ROLES.SUPER_ADMIN]}>
                    <RouteErrorBoundary>
                      <SuperAdminLayout />
                    </RouteErrorBoundary>
                  </RequireRole>
                )}
              />
              <Route
                path="/employee/*"
                element={(
                  <RequireRole role={[ROLES.EMPLOYEE, ROLES.TEAM_MANAGER]}>
                    <RouteErrorBoundary>
                      <EmployeeLayout />
                    </RouteErrorBoundary>
                  </RequireRole>
                )}
              />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
};

export default App;
